import * as React from 'react';
import { VictoryAxis, VictoryContainer } from 'victory';
import { pluck, mapAccum, merge, multiply, evolve } from 'ramda';
import { number, string, func, arrayOf, bool } from 'prop-types';

import GenericBarChartCore, {
  ChartDataType,
  TextAnchorType,
} from './GenericBarChartCore';
import ChartHScroll from '../ChartHScroll';
import { victoryTheme } from '../../theme';

/*
 * This helper function is responsible for adding 'width', and 'x'
 * properties to bars data. It expects the list of lists (groups) of any objects
 * (they can even be empty) and it adds 'width' and 'x' properties to them,
 * so sublists correspond to groups with appropriate spacing between them.
 * The result has the same structure as groupedData, only attributes to
 * objects are added.
 */
export const placeGroupedData = (
  groupedData,
  barWidth,
  barSpacing,
  groupSpacing,
) =>
  mapAccum(
    (curX, group) => [
      curX + group.length * (barWidth + barSpacing) - barSpacing + groupSpacing,
      group.map((item, pos) =>
        merge(item, {
          x: curX + pos * (barWidth + barSpacing),
          width: barWidth,
        }),
      ),
    ],
    barWidth / 2 + barSpacing / 2,
    groupedData,
  )[1];

const minWidth = 500;
const maxHScale = 2;

export const dataWidth = data =>
  Math.max.apply(null, data.map(({ x, width }) => x + width / 2));

/*
 * This is needed to make bar charts with a few bars wider.
 */
export const scaleBarsHorizontally = data => {
  const coeff = Math.min(minWidth / dataWidth(data), maxHScale);
  if (coeff < 1) {
    return [1, data];
  } else {
    const fun = multiply(coeff);
    return [
      coeff,
      data.map(
        evolve({
          width: fun,
          x: fun,
        }),
      ),
    ];
  }
};

export const margin = 24;
export const chartPadding = {
  bottom: 45,
  left: margin + 33,
  right: margin,
  top: margin + 8,
};

export const GenericBarChart = ({
  width,
  height,
  setScrollX,
  setTooltipXOffset,
  chartData,
  xTickValues,
  selectedId,
  ...restProps
}) => {
  const maxY = Math.max.apply(null, pluck('y', chartData));
  const [hScale, scaledChartData] = scaleBarsHorizontally(chartData);
  return (
    <ChartHScroll
      width={width}
      height={height}
      setScrollX={setScrollX}
      setTooltipXOffset={setTooltipXOffset}
      selectedId={selectedId}
      leftAxisElem={
        <VictoryAxis
          standalone
          containerComponent={<VictoryContainer responsive={false} />}
          padding={{ ...chartPadding, right: 0 }}
          width={chartPadding.left + 16}
          height={height - 5}
          dependentAxis
          domain={[0, maxY]}
          style={victoryTheme.commonAxis}
        />
      }
    >
      <GenericBarChartCore
        {...restProps}
        xTickValues={xTickValues.map(multiply(hScale))}
        chartData={scaledChartData}
        maxY={maxY}
        width={Math.max(width - 200, dataWidth(scaledChartData))}
        height={height - 5}
      />
    </ChartHScroll>
  );
};

GenericBarChart.propTypes = {
  width: number.isRequired,
  height: number.isRequired,
  chartData: ChartDataType.isRequired,
  xTickLabels: arrayOf(string).isRequired,
  xTickValues: arrayOf(number).isRequired,
  showVerticalGrid: bool.isRequired,
  labelAnchor: TextAnchorType.isRequired,
  onMouseOver: func.isRequired,
  onMouseOut: func.isRequired,
  onClick: func.isRequired,
  setScrollX: func.isRequired,
  setTooltipXOffset: func.isRequired,
};

export default GenericBarChart;



// WEBPACK FOOTER //
// ./src/components/GenericBarChart/GenericBarChart.jsx